import { useState } from "react";
import { useExpediente } from "@/features/revision-documental/hooks/use-revision-documental";
import {
  IconAlertTriangle,
  IconBan,
  IconCheck,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import {
  Alert,
  Button,
  Card,
  DatePickerInput,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Divider,
  Group,
  Heading,
  Stack,
  Text,
} from "@/components/ui";
import { useAuthStore, useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { tieneRolAutorizador } from "@/lib/roles";
import { parseIso, formatIso, hoyNegocio } from "@/features/datos-generales/utils/iso-date";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { useAutorizar, useDarDeBaja, useRechazar } from "../hooks/use-autorizacion-mutations";
import { MotivosForm } from "./MotivosForm";

/**
 * Card con las acciones disponibles según el rol del usuario:
 *  · Autorizador (rol genérico + AutorizadorNivelX): Autorizar / Rechazar
 *    del nivel actual si su rol específico matchea.
 *  · MesaDeControl: SIEMPRE Dar de baja (terminal). Adicionalmente, si
 *    el nivel actual exige el rol `MesaDeControl` (típicamente Nivel 3
 *    del flujo FB60), también puede Autorizar / Rechazar como cualquier
 *    autorizador.
 *  · Un usuario puede tener ambos roles.
 *
 * Los botones de Autorizar/Rechazar solo aparecen si el usuario tiene
 * alguno de los roles que el snapshot del nivel actual requiere · espejo
 * de la validación del backend (`ValidarAutorizadorPuedeOperar`).
 *
 * Cuando la solicitud ya quedó `Autorizada` (decisión P29 · Mesa de Control
 * puede dar de baja en cualquier punto vivo, incluso ya autorizada/con
 * póliza generada), el flujo ya terminó: se ocultan Autorizar/Rechazar
 * (no aplican) y solo se muestra la acción de dar de baja, con una
 * advertencia si la póliza ya se generó/depositó (el depósito SFTP no se
 * puede revertir).
 *
 * El form de motivos se inyecta inline cuando se elige rechazar o baja.
 */
export function AccionesAutorizadorCard({ id }: { id: string }) {
  const user = useAuthStore((s) => s.user);
  const { data: sol } = useSolicitud(id);
  const permisos = useUserPermisos();
  // Hotspot 90 · «Dar de baja» se decide por el PERMISO que exige el endpoint
  // (`autorizacion.dar_de_baja`), no por el rol Mesa de Control.
  const puedeDarDeBaja = permisos.includes(PERMISOS.autorizacionDarDeBaja);
  // Autorizador "de nivel" (N1/N2/Contabilidad o un rol personalizado que autoriza pero no da de
  // baja) · solo decide textos/avisos; quién firma el nivel lo decide el snapshot de roles (abajo).
  const esAutorizador =
    tieneRolAutorizador(user?.roles ?? []) ||
    (permisos.includes(PERMISOS.autorizacionAutorizar) && !puedeDarDeBaja);
  const yaAutorizada = sol.estado === "Autorizada";
  // Gestión Documental · fase 3 · mientras se revisan los documentos el flujo no avanza:
  // solo se puede dar de baja (Mesa de Control).
  const enRevisionDocumental = sol.estado === "EnRevisionDocumental";
  // Gestión Documental · fase 4 · documentos vencidos (según su configuración pueden bloquear autorizar).
  const { data: expediente } = useExpediente(id);
  const vencidos = (expediente?.documentos ?? []).filter((d) => d.vencido);

  // Filtro por nivel · espejo del backend. Aplica a cualquier rol del usuario,
  // incluyendo MesaDeControl cuando el nivel lo exige (caso Nivel 3). Una vez
  // `Autorizada`, el flujo ya terminó · no aplica autorizar/rechazar más.
  const nivelActual = sol.flujo?.niveles.find((n) => n.nivel === sol.flujo?.nivelActual);
  const rolesDelNivel = nivelActual?.rolesAutorizadores ?? [];
  const puedeAutorizarEsteNivel =
    !yaAutorizada && !enRevisionDocumental && rolesDelNivel.some((r) => user?.roles.includes(r) ?? false);
  // Hotspot 57 · «Rechazar para corrección» exige ADEMÁS el permiso autorizacion.rechazar (el
  // endpoint lo exige · P46: Mesa de Control autoriza su nivel pero no rechaza).
  const puedeRechazarEsteNivel =
    puedeAutorizarEsteNivel && permisos.includes(PERMISOS.autorizacionRechazar);

  // El último nivel del flujo (Mesa de Control) exige fijar la fecha de pago al autorizar.
  const esUltimoNivel = sol.flujo != null && sol.flujo.nivelActual === sol.flujo.cantidadNiveles;

  const [accion, setAccion] = useState<"rechazar" | "baja" | null>(null);
  const [modalFechaPago, setModalFechaPago] = useState(false);
  const [fechaPago, setFechaPago] = useState("");

  const autorizar = useAutorizar(id);
  const rechazar = useRechazar(id, { onRechazado: () => setAccion(null) });
  const baja = useDarDeBaja(id, { onBaja: () => setAccion(null) });

  return (
    <Card padding="lg">
      <Stack gap="md">
        <div>
          <Heading order={5}>
            {yaAutorizada || (puedeDarDeBaja && !esAutorizador)
              ? "Acciones de Mesa de Control"
              : "Acciones del autorizador"}
          </Heading>
          <Text size="sm" c="dimmed" className="mt-0.5">
            {yaAutorizada
              ? "La solicitud ya fue autorizada · el flujo terminó. Mesa de Control aún puede darla de baja si es necesario."
              : puedeDarDeBaja && !esAutorizador
                ? "Solo puedes dar de baja la solicitud · las decisiones de autorización las toma el autorizador."
                : "Decisión 36 · Autorizar, Rechazar para corrección. Dar de baja la solicitud requiere el permiso de dar de baja."}
          </Text>
        </div>

        <Divider />

        {sol.enviadaAContabilizacion ? (
          <Alert variant="warning" icon={<IconAlertTriangle size={16} />}>
            La póliza de esta solicitud ya se generó y el archivo ya se depositó en
            SFTP. Dar de baja NO revierte ese depósito.
          </Alert>
        ) : null}

        {accion === null ? (
          <Stack gap="sm">
            {vencidos.length > 0 && !yaAutorizada ? (
              <Alert variant="warning" icon={<IconAlertTriangle size={16} />} title="Documentos vencidos">
                {vencidos.map((d) => `${d.categoriaNombre}${d.tipoDocumento ? ` · ${d.tipoDocumento}` : ""}`).join(", ")}.
                {" "}Si su configuración lo exige, no se podrá autorizar hasta que el solicitante los reemplace por uno vigente.
              </Alert>
            ) : null}
            {enRevisionDocumental ? (
              <Alert variant="info" icon={<IconInfoCircle size={16} />}>
                La solicitud está en revisión documental · la autorización empieza cuando se aprueban
                sus documentos.
              </Alert>
            ) : null}
            {esAutorizador && !puedeDarDeBaja && !puedeAutorizarEsteNivel && !yaAutorizada && !enRevisionDocumental ? (
              <Alert variant="warning" icon={<IconInfoCircle size={16} />}>
                Esta solicitud está en el nivel {nivelActual?.nivel ?? "?"} y solo
                puede autorizarla un usuario con el rol{" "}
                <Text span fw={600}>
                  {rolesDelNivel.join(", ") || "(sin rol configurado)"}
                </Text>
                . Tu usuario no lo tiene asignado.
              </Alert>
            ) : null}
            <Group gap="xs" wrap="wrap">
              {puedeAutorizarEsteNivel ? (
                <>
                  <Button
                    className="bg-exito-600 hover:bg-exito-700"
                    leftSection={<IconCheck size={14} />}
                    loading={autorizar.isPending}
                    onClick={() => (esUltimoNivel ? setModalFechaPago(true) : autorizar.mutate(null))}
                  >
                    {esUltimoNivel ? "Autorizar y fijar fecha de pago" : "Autorizar"}
                  </Button>
                  {puedeRechazarEsteNivel ? (
                    <Button variant="default" leftSection={<IconX size={14} />} onClick={() => setAccion("rechazar")}>
                      Rechazar para corrección
                    </Button>
                  ) : null}
                </>
              ) : null}
              {puedeDarDeBaja ? (
                <Button
                  variant="default"
                  className="text-peligro-600 hover:bg-peligro-50 dark:hover:bg-peligro-900/40"
                  leftSection={<IconBan size={14} />}
                  onClick={() => setAccion("baja")}
                >
                  Dar de baja (terminal)
                </Button>
              ) : null}
              {!puedeAutorizarEsteNivel && !esAutorizador && !puedeDarDeBaja ? (
                <Text size="sm" c="dimmed">
                  Tu usuario no tiene permisos para resolver esta solicitud.
                </Text>
              ) : null}
            </Group>
          </Stack>
        ) : (
          <MotivosForm
            variante={accion}
            loading={rechazar.isPending || baja.isPending}
            onCancelar={() => setAccion(null)}
            onConfirmar={(motivos) => (accion === "rechazar" ? rechazar.mutate(motivos) : baja.mutate(motivos))}
          />
        )}
      </Stack>

      <Dialog open={modalFechaPago} onOpenChange={setModalFechaPago}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fecha de pago</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Este es el último nivel de autorización. Al autorizar debes establecer la
              fecha de pago de la solicitud.
            </Text>
            <DatePickerInput
              label="Fecha de pago"
              placeholder="Selecciona la fecha…"
              required
              minDate={hoyNegocio()}
              value={parseIso(fechaPago)}
              onChange={(d) => setFechaPago(formatIso(d))}
            />
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={() => setModalFechaPago(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-exito-600 hover:bg-exito-700"
              leftSection={<IconCheck size={14} />}
              disabled={!fechaPago}
              loading={autorizar.isPending}
              onClick={() =>
                autorizar.mutate(fechaPago, {
                  onSuccess: () => setModalFechaPago(false),
                })
              }
            >
              Autorizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
